import {
  COLOR_SPACES,
  calculateContrast,
  calculateGamutCoverage,
  colorSpaceTriangle,
  formatPercent,
  getColorSpace,
  type ChromaticityPoint,
  type ColorSpaceName,
  type GamutTriangleInput,
} from '@eyejoker/display-metrics';
import { Copy, Download, Maximize2, Monitor, Palette, RotateCcw } from 'lucide-react';
import { type CSSProperties, type HTMLAttributes, useMemo, useState } from 'react';

interface PrimaryFields {
  readonly x: string;
  readonly y: string;
  readonly lv: string;
}

interface MeterFormState {
  readonly displayName: string;
  readonly modeName: string;
  readonly red: PrimaryFields;
  readonly green: PrimaryFields;
  readonly blue: PrimaryFields;
  readonly whiteX: string;
  readonly whiteY: string;
  readonly whiteLv: string;
  readonly blackLv: string;
}

interface PatternPreset {
  readonly id: string;
  readonly label: string;
  readonly background: string;
  readonly foreground?: string;
  readonly note?: string;
  readonly windowPercent?: number;
}

const defaultState: MeterFormState = {
  displayName: '',
  modeName: 'Native / SDR',
  red: { x: '0.6879', y: '0.3054', lv: '' },
  green: { x: '0.2427', y: '0.6950', lv: '' },
  blue: { x: '0.1539', y: '0.0622', lv: '' },
  whiteX: '',
  whiteY: '',
  whiteLv: '',
  blackLv: '',
};

const patternPresets: readonly PatternPreset[] = [
  { id: 'red', label: 'Red 100%', background: '#ff0000' },
  { id: 'green', label: 'Green 100%', background: '#00ff00' },
  { id: 'blue', label: 'Blue 100%', background: '#0000ff' },
  { id: 'white', label: 'White 100%', background: '#ffffff', foreground: '#111827' },
  { id: 'black', label: 'Black 0%', background: '#000000' },
  { id: 'gray10', label: 'Gray 10%', background: 'rgb(26, 26, 26)' },
  { id: 'gray50', label: 'Gray 50%', background: 'rgb(128, 128, 128)' },
  { id: 'gray80', label: 'Gray 80%', background: 'rgb(204, 204, 204)', foreground: '#111827' },
  { id: 'window10', label: 'White Window 10%', background: '#000000', windowPercent: 10 },
  { id: 'window25', label: 'White Window 25%', background: '#000000', windowPercent: 25 },
  { id: 'window100', label: 'White Fullscreen', background: '#ffffff', foreground: '#111827' },
];

const selectedReferenceDefault: ColorSpaceName = 'DCI-P3';

export default function App() {
  const [form, setForm] = useState<MeterFormState>(defaultState);
  const [selectedReference, setSelectedReference] =
    useState<ColorSpaceName>(selectedReferenceDefault);
  const [activePattern, setActivePattern] = useState<PatternPreset | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  const gamutInput = useMemo(() => toGamutInput(form), [form]);
  const coverageResults = useMemo(
    () => (gamutInput ? calculateGamutCoverage(gamutInput) : []),
    [gamutInput],
  );
  const whiteLuminance = maybeNumber(form.whiteLv);
  const blackLuminance = maybeNumber(form.blackLv);
  const contrast =
    whiteLuminance !== null && blackLuminance !== null
      ? calculateContrast(whiteLuminance, blackLuminance)
      : null;

  const reportText = useMemo(
    () => buildReportText(form, coverageResults, contrast),
    [contrast, coverageResults, form],
  );

  const updatePrimary = (
    primary: keyof Pick<MeterFormState, 'red' | 'green' | 'blue'>,
    key: keyof PrimaryFields,
    value: string,
  ) => {
    setForm((current) => ({
      ...current,
      [primary]: {
        ...current[primary],
        [key]: value,
      },
    }));
  };

  const copyReport = async () => {
    try {
      await navigator.clipboard.writeText(reportText);
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 1400);
    } catch {
      setCopyState('failed');
      window.setTimeout(() => setCopyState('idle'), 1400);
    }
  };

  const downloadJson = () => {
    const payload = {
      tool: 'Eyejoker Display Meter',
      capturedAt: new Date().toISOString(),
      displayName: form.displayName || null,
      modeName: form.modeName || null,
      input: form,
      results: coverageResults.map((result) => ({
        colorSpace: result.colorSpace.name,
        cie1931CoveragePercent: round(result.cie1931CoveragePercent),
        cie1976CoveragePercent: round(result.cie1976CoveragePercent),
        cie1931AreaRatioPercent: round(result.cie1931AreaRatioPercent),
        cie1976AreaRatioPercent: round(result.cie1976AreaRatioPercent),
      })),
      contrast: contrast
        ? {
            whiteLuminanceCdM2: contrast.whiteLuminanceCdM2,
            blackLuminanceCdM2: contrast.blackLuminanceCdM2,
            ratio: contrast.ratio,
            label: contrast.label,
          }
        : null,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `display-meter-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (activePattern) {
    return <PatternScreen preset={activePattern} onClose={() => setActivePattern(null)} />;
  }

  return (
    <main className='app-shell'>
      <section className='hero-card'>
        <div className='eyebrow'>
          <Monitor size={18} />
          Eyejoker Display Meter
        </div>
        <div className='hero-grid'>
          <div>
            <h1>설치 없이 쓰는 수동 디스플레이 계측 보조도구</h1>
            <p>
              MK550T 같은 계측기에서 R/G/B xy 좌표만 읽어와 입력하면 색역 커버리지와 명암비를 바로
              계산합니다. 웹 패턴은 SDR/간이 확인용이며 HDR 피크는 전용 HDR10/PQ 패턴으로 교차
              확인하세요.
            </p>
          </div>
          <div className='hero-actions'>
            <button
              className='secondary-button'
              type='button'
              onClick={() => setForm(defaultState)}
            >
              <RotateCcw size={16} /> 샘플 복원
            </button>
            <button
              className='secondary-button'
              type='button'
              onClick={copyReport}
              disabled={!gamutInput}
            >
              <Copy size={16} />{' '}
              {copyState === 'copied'
                ? '복사됨'
                : copyState === 'failed'
                  ? '복사 실패'
                  : '결과 복사'}
            </button>
            <button
              className='primary-button'
              type='button'
              onClick={downloadJson}
              disabled={!gamutInput}
            >
              <Download size={16} /> JSON 저장
            </button>
          </div>
        </div>
      </section>

      <section className='layout-grid'>
        <div className='stack'>
          <section className='panel'>
            <div className='panel-heading'>
              <Palette size={18} />
              <div>
                <h2>수동 입력</h2>
                <p>계측기 화면/CSV에서 읽은 CIE1931 xy 좌표를 입력.</p>
              </div>
            </div>
            <div className='meta-grid'>
              <TextInput
                label='제품/패널명'
                value={form.displayName}
                onChange={(value) => setForm((current) => ({ ...current, displayName: value }))}
                placeholder='예: 27인치 OLED'
              />
              <TextInput
                label='모드'
                value={form.modeName}
                onChange={(value) => setForm((current) => ({ ...current, modeName: value }))}
                placeholder='예: Native / sRGB / HDR'
              />
            </div>
            <div className='primary-grid'>
              <PrimaryInput
                title='Red'
                value={form.red}
                accent='#ef4444'
                onChange={(key, value) => updatePrimary('red', key, value)}
              />
              <PrimaryInput
                title='Green'
                value={form.green}
                accent='#22c55e'
                onChange={(key, value) => updatePrimary('green', key, value)}
              />
              <PrimaryInput
                title='Blue'
                value={form.blue}
                accent='#3b82f6'
                onChange={(key, value) => updatePrimary('blue', key, value)}
              />
            </div>
            <div className='meta-grid four'>
              <TextInput
                label='White x'
                value={form.whiteX}
                onChange={(value) => setForm((current) => ({ ...current, whiteX: value }))}
                inputMode='decimal'
              />
              <TextInput
                label='White y'
                value={form.whiteY}
                onChange={(value) => setForm((current) => ({ ...current, whiteY: value }))}
                inputMode='decimal'
              />
              <TextInput
                label='White Lv'
                value={form.whiteLv}
                onChange={(value) => setForm((current) => ({ ...current, whiteLv: value }))}
                inputMode='decimal'
                suffix='cd/m²'
              />
              <TextInput
                label='Black Lv'
                value={form.blackLv}
                onChange={(value) => setForm((current) => ({ ...current, blackLv: value }))}
                inputMode='decimal'
                suffix='cd/m²'
              />
            </div>
          </section>

          <section className='panel'>
            <div className='panel-heading'>
              <Maximize2 size={18} />
              <div>
                <h2>테스트 패턴</h2>
                <p>전체 화면 패턴. 10% window는 화면 면적 기준입니다.</p>
              </div>
            </div>
            <div className='pattern-grid'>
              {patternPresets.map((preset) => (
                <button
                  key={preset.id}
                  type='button'
                  className='pattern-button'
                  onClick={() => setActivePattern(preset)}
                  style={patternButtonStyle(preset)}
                >
                  <span className='pattern-swatch' />
                  {preset.label}
                </button>
              ))}
            </div>
          </section>
        </div>

        <div className='stack'>
          <section className='panel results-panel'>
            <div className='panel-heading between'>
              <div>
                <h2>색역 결과</h2>
                <p>Coverage = 기준 색공간 내부를 덮은 비율, Area = 삼각형 면적비.</p>
              </div>
              <select
                value={selectedReference}
                onChange={(event) => setSelectedReference(event.target.value as ColorSpaceName)}
              >
                {COLOR_SPACES.map((space) => (
                  <option key={space.name} value={space.name}>
                    {space.name}
                  </option>
                ))}
              </select>
            </div>

            {gamutInput ? (
              <>
                <div className='results-table-wrap'>
                  <table className='results-table'>
                    <thead>
                      <tr>
                        <th>Color Space</th>
                        <th>CIE1931 xy</th>
                        <th>CIE1976 u'v'</th>
                        <th>Area xy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coverageResults.map((result) => (
                        <tr key={result.colorSpace.name}>
                          <td>{result.colorSpace.name}</td>
                          <td>{formatPercent(result.cie1931CoveragePercent)}</td>
                          <td>{formatPercent(result.cie1976CoveragePercent)}</td>
                          <td>{formatPercent(result.cie1931AreaRatioPercent)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ChromaticityGraph input={gamutInput} referenceName={selectedReference} />
                {contrast && (
                  <div className='contrast-card'>
                    <span>명암비</span>
                    <strong>{contrast.label}</strong>
                    <small>
                      White {contrast.whiteLuminanceCdM2} / Black {contrast.blackLuminanceCdM2}{' '}
                      cd/m²
                    </small>
                  </div>
                )}
              </>
            ) : (
              <div className='empty-state'>R/G/B xy 좌표 6개를 입력하면 결과가 표시됩니다.</div>
            )}
          </section>

          <section className='panel report-panel'>
            <h2>복사용 요약</h2>
            <pre>{reportText}</pre>
          </section>
        </div>
      </section>
    </main>
  );
}

function patternButtonStyle(
  preset: PatternPreset,
): CSSProperties & Record<'--pattern-color', string> {
  return {
    '--pattern-color': preset.windowPercent ? '#111827' : preset.background,
  };
}

function primaryAccentStyle(accent: string): CSSProperties & Record<'--accent', string> {
  return {
    '--accent': accent,
  };
}

function TextInput({
  label,
  value,
  onChange,
  placeholder,
  inputMode = 'text',
  suffix,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
  readonly suffix?: string;
}) {
  return (
    <label className='text-input'>
      <span>{label}</span>
      <div className='input-shell'>
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          inputMode={inputMode}
        />
        {suffix && <em>{suffix}</em>}
      </div>
    </label>
  );
}

function PrimaryInput({
  title,
  value,
  accent,
  onChange,
}: {
  readonly title: string;
  readonly value: PrimaryFields;
  readonly accent: string;
  readonly onChange: (key: keyof PrimaryFields, value: string) => void;
}) {
  return (
    <div className='primary-card' style={primaryAccentStyle(accent)}>
      <div className='primary-title'>{title}</div>
      <TextInput
        label='x'
        value={value.x}
        onChange={(nextValue) => onChange('x', nextValue)}
        inputMode='decimal'
      />
      <TextInput
        label='y'
        value={value.y}
        onChange={(nextValue) => onChange('y', nextValue)}
        inputMode='decimal'
      />
      <TextInput
        label='Lv'
        value={value.lv}
        onChange={(nextValue) => onChange('lv', nextValue)}
        inputMode='decimal'
        suffix='cd/m²'
      />
    </div>
  );
}

function ChromaticityGraph({
  input,
  referenceName,
}: {
  readonly input: GamutTriangleInput;
  readonly referenceName: ColorSpaceName;
}) {
  const width = 520;
  const height = 420;
  const padding = 44;
  const maxX = 0.8;
  const maxY = 0.9;
  const referenceTriangle = colorSpaceTriangle(getColorSpace(referenceName));
  const inputPoints = [input.red, input.green, input.blue] as const;
  const toSvgPoint = (point: ChromaticityPoint) => ({
    x: padding + (point.x / maxX) * (width - padding * 2),
    y: height - padding - (point.y / maxY) * (height - padding * 2),
  });
  const polygonPoints = (points: readonly ChromaticityPoint[]) =>
    points
      .map((point) => {
        const svgPoint = toSvgPoint(point);
        return `${svgPoint.x},${svgPoint.y}`;
      })
      .join(' ');

  return (
    <div className='graph-card'>
      <svg viewBox={`0 0 ${width} ${height}`} role='img' aria-label='CIE1931 xy color gamut graph'>
        <rect
          x={padding}
          y={padding}
          width={width - padding * 2}
          height={height - padding * 2}
          fill='#ffffff'
          stroke='#1f2937'
          strokeWidth='1.5'
        />
        {Array.from({ length: 9 }, (_, index) => index / 10).map((tick) => {
          const x = padding + (tick / maxX) * (width - padding * 2);
          return (
            <g key={`x-${tick}`}>
              <line
                x1={x}
                x2={x}
                y1={height - padding}
                y2={height - padding + 5}
                stroke='#64748b'
              />
              <text x={x} y={height - padding + 20} textAnchor='middle'>
                {tick.toFixed(1)}
              </text>
            </g>
          );
        })}
        {Array.from({ length: 10 }, (_, index) => index / 10).map((tick) => {
          const y = height - padding - (tick / maxY) * (height - padding * 2);
          return (
            <g key={`y-${tick}`}>
              <line x1={padding - 5} x2={padding} y1={y} y2={y} stroke='#64748b' />
              <text x={padding - 10} y={y + 4} textAnchor='end'>
                {tick.toFixed(1)}
              </text>
            </g>
          );
        })}
        <polygon
          points={polygonPoints(referenceTriangle)}
          fill='rgba(15, 23, 42, 0.08)'
          stroke='#111827'
          strokeWidth='2'
          strokeDasharray='8 5'
        />
        <polygon
          points={polygonPoints(inputPoints)}
          fill='rgba(239, 68, 68, 0.12)'
          stroke='#ef4444'
          strokeWidth='2.5'
          strokeDasharray='8 5'
        />
        {referenceTriangle.map((point) => {
          const svgPoint = toSvgPoint(point);
          return (
            <circle
              key={`${point.x}-${point.y}-ref`}
              cx={svgPoint.x}
              cy={svgPoint.y}
              r='5'
              fill='#111827'
            />
          );
        })}
        {inputPoints.map((point) => {
          const svgPoint = toSvgPoint(point);
          return (
            <circle
              key={`${point.x}-${point.y}-input`}
              cx={svgPoint.x}
              cy={svgPoint.y}
              r='5'
              fill='#ef4444'
            />
          );
        })}
      </svg>
      <div className='legend-row'>
        <span>
          <i className='legend-dot user' />
          User Input
        </span>
        <span>
          <i className='legend-dot reference' />
          {referenceName}
        </span>
      </div>
    </div>
  );
}

function PatternScreen({
  preset,
  onClose,
}: {
  readonly preset: PatternPreset;
  readonly onClose: () => void;
}) {
  const windowScale = preset.windowPercent ? Math.sqrt(preset.windowPercent / 100) : 1;
  return (
    <button
      type='button'
      className='pattern-screen'
      onClick={onClose}
      onKeyDown={(event) => {
        if (event.key === 'Escape' || event.key === 'Enter' || event.key === ' ') {
          onClose();
        }
      }}
      style={{ background: preset.background, color: preset.foreground ?? '#ffffff' }}
      autoFocus
    >
      {preset.windowPercent ? (
        <span
          className='white-window'
          style={{ width: `${windowScale * 100}vw`, height: `${windowScale * 100}vh` }}
        />
      ) : null}
      <span className='pattern-label'>{preset.label} · ESC/클릭 종료</span>
    </button>
  );
}

function toGamutInput(form: MeterFormState): GamutTriangleInput | null {
  const red = toPoint(form.red);
  const green = toPoint(form.green);
  const blue = toPoint(form.blue);

  if (!red || !green || !blue) {
    return null;
  }

  return { red, green, blue };
}

function toPoint(fields: PrimaryFields): ChromaticityPoint | null {
  const x = maybeNumber(fields.x);
  const y = maybeNumber(fields.y);
  if (x === null || y === null) {
    return null;
  }
  return { x, y };
}

function maybeNumber(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function buildReportText(
  form: MeterFormState,
  results: readonly ReturnType<typeof calculateGamutCoverage>[number][],
  contrast: ReturnType<typeof calculateContrast> | null,
): string {
  if (results.length === 0) {
    return 'R/G/B xy 좌표를 입력하면 복사용 요약이 생성됩니다.';
  }

  const lines = [
    `Display Meter${form.displayName ? ` — ${form.displayName}` : ''}${form.modeName ? ` (${form.modeName})` : ''}`,
    ...results.map(
      (result) =>
        `${result.colorSpace.name}: xy ${formatPercent(result.cie1931CoveragePercent)}, u'v' ${formatPercent(result.cie1976CoveragePercent)}, area ${formatPercent(result.cie1931AreaRatioPercent)}`,
    ),
  ];

  if (contrast) {
    lines.push(
      `Contrast: ${contrast.label} (W ${contrast.whiteLuminanceCdM2} / B ${contrast.blackLuminanceCdM2} cd/m²)`,
    );
  }

  lines.push('※ RGB 원색 좌표 기반 색역 범위 계산이며, 색 정확도/감마/HDR EOTF와는 별개입니다.');
  return lines.join('\n');
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}
